/**
 * DistributionDemoLayout — Shared chrome for the Phase 0 demo
 * =============================================================
 *
 * Simplified post-refactor:
 *   - DEMO banner (unchanged)
 *   - Compact header: title + Phase 0 badge + one-line positioning
 *   - Collapsible "Network overview" with the supply-chain diagram
 *     and full impact detail (closed by default)
 *   - Three tabs (Release / Inventory / Orders & Automation)
 *
 * Each page renders its own 3-KPI compact summary — no shared KPI
 * strip, no duplicated information.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../../../components/Header';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, Radio, Boxes, Workflow } from 'lucide-react';
import { toast } from 'sonner';
import {
    DistributionDemoProvider,
    useDistributionSimulation,
} from './DistributionDemoContext';
import NetworkOverview from './components/NetworkOverview';
import { API_BASE } from '../../../lib/apiBase';

const API = `${API_BASE}/api`;

const TABS = [
    { to: '/admin/distribution/release', label: 'Releases', icon: Radio, testid: 'tab-release-to-retail' },
    { to: '/admin/distribution/inventory', label: 'Inventory', icon: Boxes, testid: 'tab-inventory-control-tower' },
    { to: '/admin/distribution/automation', label: 'Orders & Automation', icon: Workflow, testid: 'tab-retail-order-automation' },
];

const SharedChrome = () => {
    const [intel, setIntel] = useState(null);

    useEffect(() => {
        axios.get(`${API}/admin/distribution-demo/intelligence`)
            .then(res => setIntel(res.data))
            .catch(err => {
                console.error('Failed to load intelligence payload', err);
                toast.error('Failed to load demo intelligence');
            });
    }, []);

    const sim = useDistributionSimulation(
        intel?.variants || [],
        intel?.sell_through || [],
        intel?.warehouses || [],
    );

    return (
        <>
            <NetworkOverview intel={intel} sim={sim} />
            <div className="mt-4">
                <Outlet context={{ intel, sim }} />
            </div>
        </>
    );
};

export default function DistributionDemoLayout() {
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <DistributionDemoProvider>
            <div className="min-h-screen bg-background">
                <Header />

                <div
                    data-testid="distribution-demo-banner"
                    className="bg-amber-100 border-y border-amber-300 text-amber-900"
                >
                    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span className="font-semibold">DEMO ENVIRONMENT</span>
                        <span className="hidden sm:inline text-amber-800">
                            Scenario estimates. No real orders, emails or supplier actions are triggered.
                        </span>
                    </div>
                </div>

                <main className="max-w-7xl mx-auto px-4 py-5">
                    <div className="mb-4 flex items-start justify-between flex-wrap gap-2">
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-slate-900" data-testid="distribution-demo-title">
                                    Owntune Distribution
                                </h1>
                                <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50 text-[10px] uppercase tracking-wider">
                                    Phase 0 · Demo
                                </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Orchestration platform connecting labels, manufacturers, warehouses and retailers.
                            </p>
                        </div>
                    </div>

                    <nav className="flex items-center gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
                        {TABS.map(tab => {
                            const active = location.pathname === tab.to;
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.to}
                                    data-testid={tab.testid}
                                    onClick={() => navigate(tab.to)}
                                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                        active
                                            ? 'border-slate-900 text-slate-900'
                                            : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>

                    <SharedChrome />
                </main>
            </div>
        </DistributionDemoProvider>
    );
}
